import React from 'react';
import { Form } from '@weezevent/nacre';
import { useSegment } from './tracking/trackingProvider';
import useGoogleMapsContext from '../context/googleMapsContext';
import { Dropdown } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import Styles from '../../src/styles/components/googleAddress.module.css';
import PropTypes from 'prop-types';

const useGoogleAddress = ({ onAutoComplete, onPlaceDetails }) => {
    const [google, error, loaded] = useGoogleMapsContext();
    const [addresses, setAddresses] = React.useState([]);
    const [place, setPlace] = React.useState(null);
    const [token, setToken] = React.useState(null);

    const search = React.useCallback(
        searchQuery => {
            if (error || !loaded) {
                return;
            }

            let newToken = token;
            if (!newToken) {
                newToken = new google.maps.places.AutocompleteSessionToken();
            }

            new google.maps.places.AutocompleteService().getPlacePredictions(
                {
                    input: searchQuery,
                    sessionToken: newToken
                },
                predictions => {
                    setAddresses(predictions);
                    setToken(newToken);
                }
            );
        },
        [loaded, error, setAddresses, token]
    );

    const get_details = React.useCallback(
        place_id => {
            if (error || !loaded) {
                return;
            }

            let service = new google.maps.places.PlacesService(document.querySelector('div#attribution'));
            service.getDetails(
                {
                    placeId: place_id,
                    sessionToken: token
                },
                details => {
                    let my_types = {};
                    details.address_components.forEach(({ long_name, short_name, types }) => {
                        types.forEach(type => {
                            if (type === 'political') {
                                return;
                            }
                            my_types[type] = type !== 'country' ? long_name : short_name;
                        });
                    });
                    let tmpPlace = {
                        address: {
                            address1: ((my_types?.street_number || '') + ' ' + (my_types?.route || '')).trim(),
                            address2: '',
                            city: my_types?.locality || '',
                            country: my_types?.country || '',
                            state: my_types?.administrative_area_level_1 || '',
                            zip_code: my_types?.postal_code || '',
                            place_id: place_id,
                            name: details.name || '',
                            coordinates: { lat: details.geometry.location.lat(), long: details.geometry.location.lng() }
                        },
                        full_address: details?.formatted_address
                    };
                    setPlace(tmpPlace);
                    onPlaceDetails(tmpPlace);
                    setToken(new google.maps.places.AutocompleteSessionToken());
                }
            );
        },
        [token, setPlace, onPlaceDetails]
    );

    return [addresses, place, search, get_details, error, loaded];
};

export function FooterDropdown({ onAutoModeChange }) {
    const { t } = useTranslation();

    return (
        <div className={Styles['google-address-footer-dropdown']}>
            <span className={Styles['google-address-footer-dropdown-question']} onClick={() => onAutoModeChange(false)}>
                {t('ticket.googleAddress.footerQuestion')}
                <span className={Styles['google-address-footer-dropdown-answer']}>{t('ticket.googleAddress.footerAnswer')}</span>
            </span>
            <div className={Styles['google-logo']}></div>
        </div>
    );
}

FooterDropdown.propTypes = {
    onAutoModeChange: PropTypes.func.isRequired
};

export default function GoogleAddress({ onlyAddress, onError, form, onChange, onAutoModeChange }) {
    const [addresses, place, search, get_details, error, loaded] = useGoogleAddress({
        onPlaceDetails: place => {
            onChange({ address: { ...place.address, name: form.address?.name || place.address.name }, full_address: place.full_address });
        }
    });
    const { t } = useTranslation();
    const { track } = useSegment();
    const [isLoading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setTimeout(() => setLoading(false), 500);
    }, []);

    React.useEffect(() => {
        if (!isLoading && (error || !loaded)) {
            onError();
        }
    }, [error, loaded, onError, isLoading]);

    const displayAddresses = React.useMemo(() => {
        let array = [];
        if (addresses?.length > 0) {
            array = addresses?.map(address => ({
                text: (
                    <Dropdown.Item className={'google-address-dropdown-item'} key={address.place_id}>
                        {address.description}
                    </Dropdown.Item>
                ),
                value: address.place_id,
                key: address.place_id
            }));
        }

        array.push(<FooterDropdown key="footer" onAutoModeChange={onAutoModeChange} />);

        return array;
    }, [addresses]);

    return (
        <Form.Field className={Styles['google-address-container']}>
            {!onlyAddress && (
                <Form.Field style={{ marginTop: '0' }}>
                    <Form.Label label={t('ticket.event.place')} />
                    <Form.Input
                        value={form?.address?.name}
                        onChange={event => {
                            onChange({ address: { ...place?.address, name: event.target.value } });
                        }}
                    />
                </Form.Field>
            )}
            <div id={'attribution'}></div>
            <Form.Field>
                <Form.Label label={t('ticket.googleAddress.label')} />
                <Dropdown
                    className={Styles['google-address-dropdown']}
                    closeOnChange={true}
                    selectOnNavigation={false}
                    onClick={() => track('ACTION > SELECT ADDRESS', { 'Page Title': 'CREATE EVENT' })}
                    fluid
                    clearable
                    noResultsMessage={t('ticket.googleAddress.noResultsFound')}
                    onChange={(event, address) => {
                        get_details(address.value);
                    }}
                    onSearchChange={(e, { searchQuery }) => {
                        if (searchQuery?.length > 4) {
                            search(searchQuery);
                        }
                    }}
                    options={displayAddresses}
                    placeholder={form.full_address || t('ticket.googleAddress.placeholder')}
                    search={() => displayAddresses}
                    selection
                    selectOnBlur={false}
                    value={place?.place_id}
                />
            </Form.Field>
        </Form.Field>
    );
}

GoogleAddress.propTypes = {
    onlyAddress: PropTypes.bool,
    onError: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onAutoModeChange: PropTypes.func.isRequired
};

GoogleAddress.defaultProps = {
    onlyAddress: false
};
